<template>
  <div
    class="flex flex-col border border-neutral-200 shadow-xl rounded-xl w-[416px] overflow-hidden relative max-h-[919px]"
    :class="{ 'min-h-[593px]': isPartnerLoading || (isQuoteLoading && !hasAvailability) }"
  >
    <div
      v-if="!pageReady || isPartnerLoading"
      class="w-full bg-white/75 absolute inset-0 flex items-center justify-center z-10"
    />
    <div
      v-if="showMoreTimeslots"
      class="bg-neutral-50 py-4 border-b border-neutral-200 text-center"
    >
      <vetster-search-day-picker
        is-centered
        :label="$t('search.next-available-timeslots')"
        label-classes="font-title text-xl font-bold text-neutral-900 whitespace-nowrap text-center"
      />
    </div>
    <h2
      v-else
      class="font-title text-xl text-neutral-900 px-4 pt-4 font-bold text-center whitespace-nowrap"
    >
      {{ $t('search.next-available-timeslots') }}
    </h2>
    <vetster-quote-panel
      :partner="partner"
      :default-date="defaultTimeslotDate"
      :default-timeslot="getSelectedAppointment"
      :default-timezone="timezone"
      :show-more-timeslots="showMoreTimeslots"
      :show-upsell-banner="showPaymentOption"
      is-large-screen-layout
      @show="handleShowToggle"
    />
    <div class="mt-auto quote-shadow border-t border-neutral-200">
      <template v-if="displayBanner">
        <vetster-quote-total-banner :cta-label="$t('quote.continue-booking')" />
      </template>
      <vetster-quote-total
        is-quote-panel
        is-large-screen-layout
        class="rounded-b-xl"
        @checkout="$emit('checkout')"
      />
    </div>
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { mapGetters, mapState } from 'vuex';

import VetsterQuotePanel from '@/brand-components/GuidedSearch/VetsterQuotePanel';
import VetsterQuoteTotal from '@/brand-components/GuidedSearch/VetsterQuoteTotal';
import VetsterQuoteTotalBanner from '@/brand-components/GuidedSearch/VetsterQuoteTotalBanner';
import VetsterSearchDayPicker from '@/brand-components/GuidedSearch/VetsterSearchDayPicker';
import useCheckoutHelpers from '@/composables/use-checkout-helpers';
import useSearchData from '@/composables/use-search-data';
import useSmartTime from '@/composables/use-smart-time';
import HasYearlySubscriptionPlan from '@/mixins/HasYearlySubscriptionPlan';

export default {
  name: 'VetsterPartnerQuotePanel',

  components: {
    VetsterQuotePanel,
    VetsterQuoteTotal,
    VetsterQuoteTotalBanner,
    VetsterSearchDayPicker,
  },

  mixins: [HasYearlySubscriptionPlan],

  props: {
    partner: {
      type: Object,
      required: true,
    },
    timezone: {
      type: String,
      default: undefined,
    },
    searchParams: {
      type: Object,
      default: () => ({}),
    },
  },

  setup() {
    const root = getCurrentInstance().proxy.$root;
    const { showPaymentOption } = useCheckoutHelpers(root);
    const {
      isFlatRateBookingPaygPricingExperiment,
      showSubscriptionBannerForPartner,
    } = useSearchData();
    const { validDateFrom } = useSmartTime();

    return {
      isFlatRateBookingPaygPricingExperiment,
      showPaymentOption,
      showSubscriptionBannerForPartner,
      validDateFrom,
    };
  },

  data() {
    return {
      showMoreTimeslots: false,
    };
  },

  computed: {
    ...mapState('quote', ['pageReady']),

    ...mapGetters('quote', [
      'getInputs',
      'hasAvailability',
      'isPartnerLoading',
      'isQuoteLoading',
    ]),

    ...mapGetters('subscriptions', ['canPurchaseSubscriptionPlan']),

    displayBanner() {
      return this.pageReady
        && !this.isPartnerLoading
        && this.canPurchaseSubscriptionPlan
        && this.hasAvailability
        && (this.isFlatRateBookingPaygPricingExperiment || this.showSubscriptionBannerForPartner(this.partner.appointments));
    },

    getFirstAvailableAppointment() {
      return this.partner?.appointments[0];
    },

    getSelectedAppointment() {
      if (this.getInputs.timeslot) {
        return this.getInputs.timeslot;
      }

      return this.getFirstAvailableAppointment;
    },

    defaultTimeslotDate() {
      return this.validDateFrom(this.getInputs.timeslot.start_at);
    },
  },

  methods: {
    handleShowToggle() {
      this.showMoreTimeslots = true;
    },
  },
};
</script>

<style scoped>
  .quote-shadow {
    box-shadow: 0px 0px 15px 10px #0000001A;
  }
</style>
